// Dossier magazine — ÉQUILIBRE À MOTO
// Contenu original signé Bruno · Équipe Code 2 Roues · CODE 2 ROUES · 2026
import {
  Eye, Feather, Scale, Repeat, Sparkles, Target, AlertTriangle,
  Bike, Gauge, Hand, Compass, Route, HelpCircle, Users, Wind, Lightbulb,
} from 'lucide-react';

export const equilibreMotoArticle = {
  slug: 'equilibre-moto-basse-vitesse',
  weekVolume: 36,
  weekYear: 2026,
  monthLabel: 'Septembre 2026',
  category: 'Technique & maîtrise',
  shortTitle: 'Équilibre à moto — La méthode',
  title: 'Équilibre à moto : comment le travailler à basse vitesse ?',
  kicker: '🏍️ Dossier technique · Signé Bruno · Équipe Code 2 Roues',
  subtitle:
    "À moto, <strong>l'équilibre ne dépend pas de la force</strong>. Une 200 kg peut paraître très lourde… et étonnamment légère lorsqu'elle est correctement équilibrée. Pour un élève A1/A2, comprendre cette notion change tout : moins de crispation, moins d'appréhension, des arrêts plus propres et surtout une meilleure maîtrise à allure lente.",
  heroImage: '/images/articles/equilibre-moto-hero-cercle.png',
  heroImageAlt: "Motard en équilibre à basse vitesse sur un cercle plateau — trajectoire du regard visualisée, cônes de balisage, lumière chaude de fin de journée",
  coverPoster: '/images/articles/equilibre-moto-cover-editorial.png',
  coverPosterAlt: "Couverture éditoriale CODE 2 ROUES — Équilibre à moto à basse vitesse : les clés (regard, allure, commandes, équilibre, position), le trio gagnant (embrayage, gaz, frein arrière) et la progression (grand cercle → petit cercle → huit → demi-tour)",
  posterVertical: '/images/articles/poster-equilibre-vertical.png',
  posterVerticalAlt: "Poster vertical CODE 2 ROUES — Équilibre à moto : 5 étapes pour être stable et maîtrisé (regard, position, allure, souplesse, anticipation)",
  topRightKicker: { line1: 'Technique & maîtrise', line2: 'Basse vitesse · Plateau' },
  readTimeMin: 8,
  publishedAt: '2026-09-01',
  seoDescription:
    "Comment travailler son équilibre à moto à basse vitesse ? Guide pédagogique complet : regard, point de patinage, frein arrière, 6 exercices progressifs. Par un moniteur ECSR.",
  seoKeywords:
    'équilibre moto, basse vitesse moto, point de patinage, frein arrière moto, allure lente, plateau A2, demi-tour moto, slalom moto',
  about: "Dossier pédagogique — travailler l'équilibre moto à basse vitesse pour les débutants A1/A2.",
  chapo: {
    kicker: "L'idée à retenir",
    body:
      "Chez CODE 2 ROUES, l'objectif n'est pas seulement de « tenir la moto ». On apprend à <strong>comprendre ce qui la maintient en équilibre</strong>. Cette compréhension libère l'élève de sa crispation, améliore ses arrêts, et transforme la basse vitesse en un jeu plutôt qu'une épreuve.",
    sideStat: {
      kicker: "L'idée-force",
      big: 'Moins de force,\nplus d\'équilibre',
      body: "Celui qui paraît le plus à l'aise à basse vitesse n'est pas le plus fort — c'est celui qui utilise <strong>le moins de force nécessaire</strong>.",
      accent: '#16a34a',
    },
  },
  pullQuote: {
    text: "Ne cherchez pas immédiatement à réussir un parcours lent difficile. Sentez votre moto. Apprenez son poids. Trouvez son équilibre. Puis réduisez progressivement l'allure.",
    source: 'Bruno · Équipe Code 2 Roues — Enseignant ECSR, CODE 2 ROUES',
  },
  sections: [
    {
      part: 'Partie 1 — Comprendre',
      title: "L'équilibre à moto : deux situations à distinguer",
      intro: "Avant tout exercice, il faut identifier <strong>quel équilibre on travaille</strong>. Ce sont deux mondes différents.",
      cards: [
        {
          icon: Scale,
          accent: '#0ea5e9',
          kicker: 'Situation A',
          title: 'Équilibre STATIQUE',
          body: "Moto à l'arrêt ou quasi-immobile. On le retrouve à la <strong>prise en main</strong>, en <strong>déplacement moteur coupé</strong>, à <strong>l'arrêt</strong>, en <strong>manœuvre</strong>, à la <strong>mise/retrait de la béquille</strong>, et dans les <strong>premières secondes avant démarrage</strong>.",
        },
        {
          icon: Gauge,
          accent: '#16a34a',
          kicker: 'Situation B',
          title: 'Équilibre DYNAMIQUE',
          body: "Moto en mouvement. Plus la vitesse augmente, plus la moto se stabilise <strong>d'elle-même</strong>. À très basse vitesse, en revanche, le conducteur doit intervenir avec le <strong>regard + la direction + les commandes + les appuis + l'allure</strong>.",
        },
      ],
      cardsCols: 2,
      callouts: [
        {
          icon: Lightbulb,
          accent: '#f59e0b',
          body: "<strong>Le paradoxe :</strong> ce qui semble le plus dur (rouler très lentement) demande <em>plus</em> d'action du conducteur que ce qui semble le plus intimidant (rouler vite en ligne droite). Voilà pourquoi on travaille le plateau AVANT la circulation.",
        },
      ],
    },
    {
      part: 'Partie 2 — La sensation',
      title: 'Une moto bien équilibrée devient « légère »',
      intro: "C'est probablement la première sensation à faire découvrir. Moto droite, son centre de gravité reste au-dessus de sa zone d'appui au sol : elle demande très peu d'effort. Dès qu'elle s'incline suffisamment, son poids crée un couple de renversement — et là, 200 kg restent 200 kg.",
      callouts: [
        {
          icon: Target,
          accent: '#16a34a',
          body: "<strong>🎯 EXERCICE — Trouver le point d'équilibre.</strong> Moteur coupé, placez-vous à côté de la moto en zone plane. Tenez-la et redressez-la progressivement. Faites-la légèrement évoluer autour de sa position verticale. Cherchez cette sensation : <em>« Je ne porte presque plus la moto. »</em> Vous venez de trouver sa zone d'équilibre. Objectif : quelques degrés suffisent. Aucune inclinaison forte.",
        },
      ],
    },
    {
      part: 'Partie 3 — Corriger',
      title: "L'erreur n°1 du débutant : vouloir retenir la moto avec les bras",
      intro: "Manque de confiance = crispation. On serre le guidon, on tend les bras, on contracte les épaules, on regarde la moto, on lutte contre son poids. <strong>Mauvaise stratégie.</strong> Plus on se crispe, moins on ressent les mouvements de la moto.",
      compareCards: [
        {
          accent: '#cf2031',
          kicker: 'Réflexe du débutant',
          title: 'FORCE + CRISPATION',
          intro: "Ce qui se passe quand on lutte contre la moto :",
          points: [
            { ok: false, text: 'Bras tendus, épaules contractées' },
            { ok: false, text: 'Regard fixé sur la roue avant' },
            { ok: false, text: 'Guidon serré comme si on portait la moto' },
            { ok: false, text: 'On sent moins les mouvements de la machine' },
          ],
        },
        {
          accent: '#16a34a',
          kicker: 'La bonne logique',
          title: 'ÉQUILIBRE + SOUPLESSE + ANTICIPATION',
          intro: "Le partage des rôles corps/moto :",
          points: [
            { ok: true, text: 'Les bras GUIDENT (ne portent pas)' },
            { ok: true, text: 'Le corps ACCOMPAGNE le mouvement' },
            { ok: true, text: 'Les jambes assurent les APPUIS' },
            { ok: true, text: 'Le regard PRÉPARE la trajectoire' },
          ],
        },
      ],
    },
    {
      part: 'Partie 4 — Les 3 outils invisibles',
      title: 'Regard, point de patinage, frein arrière',
      intro: "Trois techniques discrètes qui font 80 % de la différence entre un débutant crispé et un élève à l'aise à basse vitesse.",
      cards: [
        {
          icon: Eye,
          accent: '#0ea5e9',
          kicker: 'Outil n°1',
          title: 'Le REGARD — ton meilleur allié',
          body: "Erreur classique : regarder juste devant la roue avant. Ton regard trop proche perturbe ta capacité à anticiper. <strong>La règle CODE 2 ROUES : regarde LÀ où tu veux aller.</strong> Dans un virage lent ou un demi-tour, tourne la tête, cherche la sortie, oriente la moto — ton corps accompagne.",
        },
        {
          icon: Hand,
          accent: '#f97316',
          kicker: 'Outil n°2',
          title: 'Le POINT DE PATINAGE',
          body: "À très basse vitesse, l'équilibre repose sur une vitesse faible mais <strong>régulière</strong>. Trois commandes essentielles : <strong>embrayage + accélérateur + frein arrière</strong>. Crée une légère traction tout en contrôlant précisément l'allure. On travaille autour du point de patinage.",
        },
        {
          icon: Feather,
          accent: '#7c3aed',
          kicker: 'Outil n°3',
          title: 'Le FREIN ARRIÈRE — l\'arme secrète',
          body: "Il permet de ralentir et de réguler avec finesse. Stabilise l'allure quand le moteur continue à transmettre. Fonctionnement type : <em>un peu de motricité → point de patinage → régulation au frein arrière</em>. ⚠️ Un frein avant brutal + guidon braqué = déséquilibre rapide. Progressivité, toujours.",
        },
      ],
      cardsCols: 3,
    },
    {
      part: 'Partie 5 — Les 6 exercices',
      title: 'Ta progression complète, exercice par exercice',
      intro: "Voici l'ordre optimal pour transformer la théorie en automatismes. Chaque exercice construit le suivant — ne saute pas d'étape.",
      orderedList: [
        "<strong>Exercice 1 — Marcher avec sa moto (moteur coupé).</strong> Avant même de rouler, apprends à manipuler la machine. Ligne droite, puis arrêt, redémarrage, changements de direction, marche arrière, petites manœuvres. Ne cherche pas à <em>porter</em> la moto — cherche à la conserver près de son point d'équilibre.",
        "<strong>Exercice 2 — Les premiers mètres.</strong> Sur la moto, pieds au sol, moteur démarré, 1ʳᵉ engagée. Cherche le point de patinage. Quand la moto avance, accompagne-la quelques mètres, débraye, arrête-toi proprement. Recommence. Séquence à automatiser : <strong>Départ → Équilibre → Arrêt.</strong>",
        "<strong>Exercice 3 — Lever les pieds.</strong> Une fois les départs réguliers, dès que la moto a suffisamment d'allure, les pieds retrouvent les repose-pieds. Un pied qui reste trop bas devient une fausse sécurité. Fais confiance à la moto en mouvement.",
        "<strong>Exercice 4 — La ligne droite lente.</strong> Deux repères éloignés. Cherche d'abord une allure lente <strong>stable et reproductible</strong> (pas la plus lente immédiatement). Travaille : <em>Regard loin → Point de patinage → Gaz stable → Frein arrière progressif</em>. Puis réduis l'allure.",
        "<strong>Exercice 5 — Le slalom large.</strong> Cônes bien espacés. Pas de chrono. Cherche : le regard, la souplesse des bras, la régularité, l'orientation de la tête, la maîtrise de l'allure. Puis réduis progressivement l'espacement. 🎯 La difficulté doit venir progressivement.",
        "<strong>Exercice 6 — Le demi-tour.</strong> Concentre l'essentiel des difficultés basse vitesse. L'erreur classique : regarder le sol, le cône, la roue avant, l'obstacle à éviter. Au contraire : <strong>tourne la tête et cherche la sortie</strong>. Conserve une légère motricité, régule l'allure, laisse la moto tourner. Ne fixe JAMAIS l'obstacle.",
      ],
    },
    {
      part: 'Partie 6 — Adaptations',
      title: 'Petit gabarit, terrain, mental : les cas particuliers',
      intro: "L'équilibre n'est pas qu'une question de technique — c'est aussi une question d'adaptation à ton corps et à ton environnement.",
      cards: [
        {
          icon: Users,
          accent: '#ec4899',
          kicker: 'Petits gabarits',
          title: 'Un appui solide > deux appuis précaires',
          body: "Être petit ne signifie pas être incapable de maîtriser une A2. Il faut simplement adapter la technique. À l'arrêt, chercher à poser les 2 pieds entiers au sol n'est pas toujours nécessaire. Prépare ton arrêt, garde la moto bien orientée, choisis ton pied d'appui, décale légèrement le bassin si besoin, pose franchement un pied. <strong>Un appui solide vaut mieux que deux appuis précaires.</strong>",
        },
        {
          icon: Wind,
          accent: '#eab308',
          kicker: 'Environnement',
          title: 'Avant de t\'arrêter, observe le terrain',
          body: "L'équilibre commence <strong>avant</strong> l'arrêt. À surveiller : dévers · gravillons · trou · plaque métallique · peinture glissante · sol humide · bordure · pente. Tu peux maîtriser parfaitement ta moto et être surpris parce que ton pied atterrit 5 cm plus bas que prévu.",
        },
        {
          icon: HelpCircle,
          accent: '#7c3aed',
          kicker: 'Le mental',
          title: "L'équilibre est aussi une question de CONFIANCE",
          body: "<em>« La moto est trop lourde, je vais tomber »</em> → crispation → perte de précision → déséquilibre → appréhension renforcée. Cercle vicieux à casser. La progression : <strong>Comprendre → Ressentir → Réussir → Répéter → Automatiser.</strong>",
        },
      ],
      cardsCols: 3,
    },
    {
      part: 'À retenir',
      title: 'Les 8 piliers de l\'équilibre moto',
      intro: "Résumé rapide à emporter dans ton casque avant chaque séance plateau :",
      cards: [
        { icon: Eye, accent: '#0ea5e9', kicker: '👀', title: 'REGARD', body: "Regarder loin et vers la destination." },
        { icon: Feather, accent: '#16a34a', kicker: '👐', title: 'DÉCONTRACTION', body: "Éviter de verrouiller les bras." },
        { icon: Scale, accent: '#eab308', kicker: '⚖️', title: 'ÉQUILIBRE', body: "Comprendre le comportement et le poids de sa moto." },
        { icon: Hand, accent: '#f97316', kicker: '✋', title: 'EMBRAYAGE', body: "Maîtriser précisément le point de patinage." },
        { icon: Gauge, accent: '#cf2031', kicker: '✊', title: 'ACCÉLÉRATEUR', body: "Motricité régulière quand nécessaire." },
        { icon: Compass, accent: '#7c3aed', kicker: '🦶', title: 'FREIN ARRIÈRE', body: "Réguler finement l'allure." },
        { icon: Bike, accent: '#ec4899', kicker: '🧍', title: 'APPUIS', body: "Préparer ses arrêts plutôt que les subir." },
        { icon: Repeat, accent: '#16a34a', kicker: '🔁', title: 'RÉPÉTITION', body: "Transformer la technique en automatisme." },
      ],
      cardsCols: 4,
    },
  ],
  ctaFinal: {
    kicker: 'Passe à la pratique',
    title: 'Ouvre ton livret Plateau interactif',
    body: "Tous ces exercices sont détaillés étape par étape dans notre <strong>Livret Plateau A1/A2</strong> avec cases à cocher, PDF imprimable et sommaire par manœuvre. 100 % gratuit.",
    primary: { label: 'Ouvrir le livret Plateau', to: '/livret-plateau' },
    secondary: { label: 'Voir toutes les fiches plateau', to: '/epreuves/plateau' },
  },
};
